import { ChatConfig } from '../types/types';
import AiChat from './aiChat/AiChat';
import useInnovachat from '../hooks/useInnovachat';
import { useEffect, useRef, useState } from 'react';
import ReportDialog from './report/ReportDialog';
import ChatButton from './chatButton/ChatButton';

const PlatformAiChat: React.FC<{ props: ChatConfig }> = ({ props }) => {
  const [chatVisible, setChatVisible] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const lastScrollTop = useRef<number>(0);

  const [isReportDialogOpen, setIsReportDialogOpen] = useState(false);
  const [reportHtmlContent, setReportHtmlContent] = useState('');

  const {
    messages,
    loading,
    sendMessage,
    artifacts,
    pendingToolApprovals,
    reviewApprovals,
    clearConversation,
    conversations,
    currentConversationId,
    createNewConversation,
    selectConversation,
    deleteConversation,
  } = useInnovachat({
    jwt: props.jwt,
    llmConfigId: props.llmConfigId,
    collections: props.collections,
    apiIds: props.apiIds,
    initialMessages: props.messages,
    dynamicParams: props.dynamicParams,
    backendUrl: props.backendUrl,
    authInstanceId: props.authInstanceId,
  });

  const handleArtifactClick = (artifactId: string): void => {
    const artifact = artifacts[artifactId];
    if (artifact && artifact.type == 'report') {
      const htmlContent = artifact.data;
      setReportHtmlContent(htmlContent);
      setIsReportDialogOpen(true);
      setChatVisible(false);
    }
  };

  const handleCloseReportDialog = (): void => {
    setIsReportDialogOpen(false);
  };

  const [isMobile] = useState(window.innerWidth < 768);
  const [currentStatusMessage, setCurrentStatusMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!props.statusMessages || props.statusMessages.length === 0 || chatVisible) {
      setCurrentStatusMessage(null);
      return;
    }

    const intervalTime = (props.statusInterval || 30) * 1000;
    const durationTime = (props.statusDuration || 8) * 1000;

    const showMessage = (): void => {
      const randomIndex = Math.floor(Math.random() * props.statusMessages!.length);
      setCurrentStatusMessage(props.statusMessages![randomIndex]);

      setTimeout(() => {
        setCurrentStatusMessage(null);
      }, durationTime);
    };

    const interval = setInterval(showMessage, intervalTime);

    // Show initial message after a shorter delay
    const initialTimeout = setTimeout(showMessage, 5000);

    return () => {
      clearInterval(interval);
      clearTimeout(initialTimeout);
    };
  }, [chatVisible, props.statusMessages, props.statusInterval, props.statusDuration]);

  return (
    <>
      <ChatButton
        onClick={() => setChatVisible(!chatVisible)}
        visible={!chatVisible || (!isMobile && chatVisible)}
        isOpen={chatVisible}
        icon={props.chatButtonIcon}
        primaryColor={props.primaryColor}
        statusMessage={currentStatusMessage}
      />

      <ReportDialog
        htmlContent={reportHtmlContent}
        isOpen={isReportDialogOpen}
        onClose={handleCloseReportDialog}
      />
      <AiChat
        artifacts={artifacts}
        apiIds={props.apiIds}
        llmConfigId={props.llmConfigId}
        authInstanceId={props.authInstanceId}
        collections={props.collections}
        messages={messages}
        loading={loading}
        sendMessage={sendMessage}
        onArtifactClick={handleArtifactClick}
        chatVisible={chatVisible}
        setChatVisible={setChatVisible}
        isFullscreen={isFullscreen}
        setIsFullscreen={setIsFullscreen}
        lastScrollTop={lastScrollTop}
        clearConversation={clearConversation}
        pendingToolApprovals={pendingToolApprovals}
        reviewApprovals={reviewApprovals}
        primaryColor={props.primaryColor}
        secondaryColor={props.secondaryColor}
        aiMessageIcon={props.aiMessageIcon}
        loadingLottieUrl={props.loadingLottieUrl}
        conversations={conversations}
        currentConversationId={currentConversationId}
        createNewConversation={createNewConversation}
        selectConversation={selectConversation}
        deleteConversation={deleteConversation}
      />
    </>
  );
};

export default PlatformAiChat;

